import React, { useEffect, useRef, useState } from 'react';
import { Button, Radio, Space, Spin, Typography } from 'antd';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { groupSlots } from 'modules/utilities/utility';
import { getSlotApi } from 'modules/dashboards/actionables/api';
import SlotTabs from 'modules/slot_tab/slotsTabs';
import { SLOT_NOT_CATERED_RESOLUTION } from './constants';

const { Text } = Typography;

/**
 * Resolution step for Hair Coach Issues -> Slot not Catered.
 *
 * The coach closes the concern inside the same flow that raises the ticket: either the customer was
 * already helped on the call, or a fresh coach slot is booked for them. Either way the footer closes
 * the ticket immediately after creating it, which is the whole point of the subcategory — these
 * tickets used to be raised and then sit Open with nothing left to do on them.
 *
 * The slot list is the SAME one the Slot-Booking modal shows (ConsultDoctorCanvas), so a coach sees
 * identical availability in both places.
 *
 * Rendered in two places: the create panel (SubcategorySelect) and the "Ticket Already Exists"
 * escalation panel (CreateTicketV2), where the resolution is applied to the existing ticket instead
 * of a new one. The component itself is identical in both — only the caller differs.
 */
// The create panel drives its Create Ticket button off a shared validation map; the escalation
// panel has no such map and gates its own button instead. Defaulting to a no-op lets both mount
// this component unchanged.
const noop = () => {};

const SlotNotCateredResolution = ({
    caseId,
    setValidation = noop,
    onResolutionChange = noop,
    onSlotChange = noop,
}) => {
    const [resolution, setResolution] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    // Slots are held locally rather than in useConsultDoctor's shared allSlotData slice. That slice
    // backs ~20 other widgets — including ConsultDoctorCanvas, which EngagementHeader can have open
    // at the same time as this panel — so fetching into it would overwrite what a sibling widget is
    // displaying, and clearing it on unmount would blank it outright.
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);

    // One session id per mount, shared by the slot fetch and the booking that follows it — the
    // booking is rejected if it does not carry the session the slots were offered under.
    const sessionIdRef = useRef(uuidv4());
    // Guards against a slow first fetch resolving after unmount, or after a 15-day refetch has
    // already replaced its results.
    const activeFetchRef = useRef(0);

    useEffect(() => {
        setValidation((prev) => ({ ...prev, slot_not_catered_resolution: false }));

        return () => {
            activeFetchRef.current += 1;
            // The keys are required only while this subcategory is selected. Leaving them behind
            // would disable Create Ticket for every other subcategory the agent switches to.
            setValidation((prev) => {
                const { slot_not_catered_resolution, coach_slot, ...rest } = prev;
                return rest;
            });
            onResolutionChange(null);
            onSlotChange(null);
        };
        // Mount-only: this registers the validation keys once and tears them down on unmount.
        // Depending on the setter props would re-run it on every parent render and re-add keys
        // the cleanup has already removed.
        //
        // No eslint-disable comment: react-hooks is not among this project's registered plugins,
        // so a disable for `react-hooks/exhaustive-deps` is itself a build error
        // ("Definition for rule ... was not found"). Same trap documented in
        // treatment_efficacy/audited/AuditedCustomers.jsx.
    }, []);

    // Same call the Slot-Booking modal makes (ConsultDoctorCanvas -> fetchSlotData with slot=true):
    // GET v3/slots/{caseId}?session_id=…&lead_follow_up=false&source=web
    const loadSlots = async (type = '') => {
        const fetchId = activeFetchRef.current + 1;
        activeFetchRef.current = fetchId;
        setSlotsLoading(true);
        try {
            const response = await getSlotApi(caseId, sessionIdRef.current, false, true, type);
            if (activeFetchRef.current !== fetchId) return;
            setSlots(_.get(response, ['data'], []));
        } catch (error) {
            if (activeFetchRef.current !== fetchId) return;
            const fallbackError = _.get(error, ['message'], 'Failed to load coach slots');
            toast.error(_.get(error, ['response', 'data', 'message'], fallbackError));
            setSlots([]);
        } finally {
            if (activeFetchRef.current === fetchId) setSlotsLoading(false);
        }
    };

    const onResolutionSelect = (value) => {
        setResolution(value);
        onResolutionChange(value);
        setValidation((prev) => ({ ...prev, slot_not_catered_resolution: true }));

        // A slot picked before the agent changed their mind must not travel with a "resolved"
        // ticket — it would book a call nobody asked for.
        setSelectedSlot(null);
        onSlotChange(null);

        if (value === SLOT_NOT_CATERED_RESOLUTION.NEEDS_COACH) {
            setValidation((prev) => ({ ...prev, coach_slot: false }));
            loadSlots();
        } else {
            setValidation((prev) => {
                const { coach_slot, ...rest } = prev;
                return rest;
            });
        }
    };

    const onSlotSelection = (slot) => {
        setSelectedSlot(slot);
        onSlotChange({ slot, sessionId: sessionIdRef.current });
        setValidation((prev) => ({ ...prev, coach_slot: true }));
    };

    const onFifteenDaySlots = () => {
        loadSlots('&is_15_days_slot_required=true');
    };

    const needsCoach = resolution === SLOT_NOT_CATERED_RESOLUTION.NEEDS_COACH;
    const isResolved = resolution === SLOT_NOT_CATERED_RESOLUTION.RESOLVED;

    return (
        <div className="my-3">
            <Text strong>Resolution</Text>
            <div className="mt-2">
                <Radio.Group
                    value={resolution}
                    onChange={(e) => onResolutionSelect(e.target.value)}
                >
                    <Space direction="vertical">
                        <Radio value={SLOT_NOT_CATERED_RESOLUTION.RESOLVED}>
                            Customer Concern Resolved
                        </Radio>
                        <Radio value={SLOT_NOT_CATERED_RESOLUTION.NEEDS_COACH}>
                            Customer Still Needs to Speak to Coach
                        </Radio>
                    </Space>
                </Radio.Group>
            </div>

            {/* Both outcomes close the ticket, so both say so — an agent picking "resolved" should
                not have to infer that from the absence of a message. */}
            {isResolved && (
                <Text type="success">
                    Concern resolved. The ticket will be created with your comment and closed
                    automatically.
                </Text>
            )}

            {needsCoach && (
                <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Text strong>Select a slot</Text>
                        <Button size="small" onClick={onFifteenDaySlots} disabled={slotsLoading}>
                            Get 15 Days Slots
                        </Button>
                    </div>

                    {slotsLoading ? (
                        <div className="d-flex justify-content-center py-3">
                            <Spin />
                        </div>
                    ) : (
                        <div className="d-flex flex-wrap justify-content-center">
                            <SlotTabs data={groupSlots(slots)} onSlotSelection={onSlotSelection} />
                        </div>
                    )}

                    {selectedSlot && (
                        <Text type="success">
                            Slot selected. The ticket will be closed once this slot is booked.
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
};

export default SlotNotCateredResolution;
